import React, { useState } from 'react';
import { BrowserRouter, Routes, Route, Link, useNavigate } from 'react-router-dom';
import './App.css';
import { AuthProvider, useAuth } from './context/AuthContext';
import * as api from './utils/api';
import TarotCard from './components/TarotCard';
import { downloadAsImage, downloadAsPDF, shareReading } from './utils/downloadReading';
import Subscription from './pages/Subscription';
import AdminPanel from './pages/AdminPanel';
import CryptoTutorial from './pages/CryptoTutorial';
import InstallPWA from './components/InstallPWA';
import Settings from './pages/Settings';

// Auth Modal Component
function AuthModal({ isOpen, onClose, mode: initialMode = 'login' }) {
  const [mode, setMode] = useState(initialMode);
  const [email, setEmail] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { loginUser } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      let response;
      if (mode === 'login') {
        response = await api.login(email, password);
      } else {
        response = await api.register(email, username, password);
      }
      
      loginUser(response.access_token, response.user);
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <h2 className="text-center mb-4 glow-text" style={{fontSize: '2rem'}}>
          {mode === 'login' ? '🔮 Welcome Back' : '✨ Join Bitarot'}
        </h2>
        
        {error && <div className="error-message">{error}</div>}
        
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label">Email</label>
            <input
              type="email"
              className="form-input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              data-testid="auth-email-input"
            />
          </div>
          
          {mode === 'register' && (
            <div className="form-group">
              <label className="form-label">Username</label>
              <input
                type="text"
                className="form-input"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
                data-testid="auth-username-input"
              />
            </div>
          )}
          
          <div className="form-group">
            <label className="form-label">Password</label>
            <input
              type="password"
              className="form-input"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              data-testid="auth-password-input"
            />
          </div>
          
          <button 
            type="submit" 
            className="btn btn-primary" 
            style={{width: '100%', marginBottom: '1rem'}}
            disabled={loading}
            data-testid="auth-submit-button"
          >
            {loading ? 'Processing...' : mode === 'login' ? 'Sign In' : 'Create Account'}
          </button>
        </form>
        
        <p className="text-center" style={{color: '#cbd5e1'}}>
          {mode === 'login' ? "Don't have an account? " : 'Already have an account? '}
          <span 
            onClick={() => setMode(mode === 'login' ? 'register' : 'login')}
            style={{color: '#8b5cf6', cursor: 'pointer', fontWeight: 'bold'}}
            data-testid="auth-toggle-mode"
          >
            {mode === 'login' ? 'Sign Up' : 'Sign In'}
          </span>
        </p>
      </div>
    </div>
  );
}

// Navigation Component
function Navigation() {
  const { user, isAuthenticated, logoutUser } = useAuth();
  const [showAuthModal, setShowAuthModal] = useState(false);
  const navigate = useNavigate();

  return (
    <>
      <nav className="navbar">
        <div className="logo" onClick={() => navigate('/')}>
          🔮 Bitarot
        </div>
        <div className="nav-links">
          {isAuthenticated ? (
            <>
              <Link to="/dashboard" className="nav-link" data-testid="nav-dashboard">Dashboard</Link>
              <Link to="/reading" className="nav-link" data-testid="nav-reading">New Reading</Link>
              <Link to="/history" className="nav-link" data-testid="nav-history">History</Link>
              <Link to="/cards" className="nav-link" data-testid="nav-cards">Card Library</Link>
              <Link to="/subscription" className="nav-link" data-testid="nav-subscription">💎 Upgrade</Link>
              {user?.is_admin && (
                <Link to="/admin" className="nav-link" data-testid="nav-admin" style={{color: '#f59e0b'}}>👑 Admin</Link>
              )}
              <Link to="/settings" className="nav-link" data-testid="nav-settings" style={{color: '#8b5cf6'}}>⚙️ {user?.username}</Link>
              <button onClick={logoutUser} className="btn btn-secondary" data-testid="nav-logout">
                Logout
              </button>
            </>
          ) : (
            <>
              <Link to="/" className="nav-link">Home</Link>
              <button onClick={() => setShowAuthModal(true)} className="btn btn-primary" data-testid="nav-login">
                Get Started
              </button>
            </>
          )}
        </div>
      </nav>
      <AuthModal isOpen={showAuthModal} onClose={() => setShowAuthModal(false)} />
    </>
  );
}

// Home Page
function Home() {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [showAuthModal, setShowAuthModal] = useState(false);

  return (
    <div className="fade-in">
      <div className="mystical-bg"></div>
      <div className="hero">
        <h1 className="hero-title glow-text" data-testid="hero-title">Bitarot</h1>
        <p className="hero-subtitle">
          Where Crypto Meets Mysticism ✨ Discover Your Blockchain Destiny
        </p>
        <p style={{fontSize: '1.2rem', color: '#cbd5e1', marginBottom: '3rem'}}>
          Personalized tarot readings powered by AI, themed with the world of cryptocurrency.
          <br/>Let the Bitarot Reader guide your journey through the crypto realm.
        </p>
        {isAuthenticated ? (
          <button 
            onClick={() => navigate('/dashboard')} 
            className="btn btn-primary" 
            style={{fontSize: '1.2rem', padding: '1rem 2rem'}}
            data-testid="hero-dashboard-button"
          >
            Enter the Oracle 🔮
          </button>
        ) : (
          <button 
            onClick={() => setShowAuthModal(true)} 
            className="btn btn-primary" 
            style={{fontSize: '1.2rem', padding: '1rem 2rem'}}
            data-testid="hero-get-started-button"
          >
            Begin Your Journey 🚀
          </button>
        )}
      </div>
      
      <div className="grid grid-3" style={{paddingTop: '2rem'}}>
        <div className="card">
          <div style={{fontSize: '3rem', textAlign: 'center', marginBottom: '1rem'}}>🎴</div>
          <h3 style={{fontSize: '1.5rem', marginBottom: '1rem', textAlign: 'center'}}>78 Bitarot Cards</h3>
          <p style={{textAlign: 'center', color: '#cbd5e1'}}>
            Custom crypto-themed tarot deck featuring Bitcoin Kings, Ethereum Queens, Solana Knights, and more!
          </p>
        </div>
        
        <div className="card">
          <div style={{fontSize: '3rem', textAlign: 'center', marginBottom: '1rem'}}>🤖</div>
          <h3 style={{fontSize: '1.5rem', marginBottom: '1rem', textAlign: 'center'}}>AI-Powered Readings</h3>
          <p style={{textAlign: 'center', color: '#cbd5e1'}}>
            Get personalized interpretations from the Bitarot Reader based on your specific questions.
          </p>
        </div>
        
        <div className="card">
          <div style={{fontSize: '3rem', textAlign: 'center', marginBottom: '1rem'}}>📊</div>
          <h3 style={{fontSize: '1.5rem', marginBottom: '1rem', textAlign: 'center'}}>Multiple Spreads</h3>
          <p style={{textAlign: 'center', color: '#cbd5e1'}}>
            Choose from single card, three-card spread, or the comprehensive Celtic Cross reading.
          </p>
        </div>
      </div>
      
      <AuthModal isOpen={showAuthModal} onClose={() => setShowAuthModal(false)} />
    </div>
  );
}

// Dashboard Page
function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [dailyCard, setDailyCard] = useState(null);
  const [loading, setLoading] = useState(true);

  React.useEffect(() => {
    loadDailyCard();
  }, []);

  const loadDailyCard = async () => {
    try {
      const card = await api.getDailyCard();
      setDailyCard(card);
    } catch (error) {
      console.error('Error loading daily card:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container fade-in" data-testid="dashboard-page">
      <div className="mystical-bg"></div>
      <h1 className="text-center mb-4 glow-text" style={{fontSize: '3rem'}}>
        Welcome, {user?.username}! ✨
      </h1>
      
      <div className="grid grid-2">
        <div className="card">
          <h2 style={{marginBottom: '1rem', fontSize: '1.8rem'}}>🌅 Today's Daily Card</h2>
          {loading ? (
            <div className="spinner"></div>
          ) : dailyCard ? (
            <div data-testid="daily-card-display">
              <div style={{marginBottom: '1rem'}}>
                <TarotCard 
                  card={dailyCard.card} 
                  isReversed={dailyCard.is_reversed}
                  testId="daily-card"
                />
              </div>
              <p style={{color: '#cbd5e1', lineHeight: '1.6'}}>{dailyCard.interpretation}</p>
            </div>
          ) : (
            <p>No daily card available</p>
          )}
        </div>
        
        <div className="card">
          <h2 style={{marginBottom: '1.5rem', fontSize: '1.8rem'}}>🔮 Quick Actions</h2>
          <div style={{display: 'flex', flexDirection: 'column', gap: '1rem'}}>
            <button 
              onClick={() => navigate('/reading')} 
              className="btn btn-primary"
              data-testid="quick-action-new-reading"
            >
              ✨ New Reading
            </button>
            <button 
              onClick={() => navigate('/history')} 
              className="btn btn-secondary"
              data-testid="quick-action-history"
            >
              📜 View Reading History
            </button>
            <button 
              onClick={() => navigate('/cards')} 
              className="btn btn-secondary"
              data-testid="quick-action-card-library"
            >
              📚 Browse Card Library
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Reading Page
function Reading() {
  const navigate = useNavigate();
  const [question, setQuestion] = useState('');
  const [spreadType, setSpreadType] = useState('single');
  const [loading, setLoading] = useState(false);
  const [reading, setReading] = useState(null);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    setReading(null);

    try {
      const result = await api.createReading(question, spreadType);
      setReading(result);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create reading');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadImage = async () => {
    await downloadAsImage('reading-content', `bitarot-reading-${Date.now()}.png`);
  };

  const handleDownloadPDF = async () => {
    await downloadAsPDF('reading-content', `bitarot-reading-${Date.now()}.pdf`);
  };

  const handleShare = async () => {
    await shareReading('reading-content', 'My Bitarot Reading');
  };

  if (reading) {
    return (
      <div className="container fade-in" data-testid="reading-result">
        <div className="mystical-bg"></div>
        <h1 className="text-center mb-4 glow-text" style={{fontSize: '3rem'}}>
          Your Bitarot Reading
        </h1>
        
        <div className="card" style={{maxWidth: '900px', margin: '0 auto'}} id="reading-content">
          <h2 style={{marginBottom: '1rem', color: '#8b5cf6'}}>Question:</h2>
          <p style={{fontSize: '1.2rem', marginBottom: '2rem', color: '#cbd5e1'}}>{reading.question}</p>
          
          <h3 style={{marginBottom: '1.5rem', fontSize: '1.5rem'}}>Cards Drawn:</h3>
          <div className={`grid ${reading.cards.length > 3 ? 'grid-4' : 'grid-3'}`} style={{marginBottom: '2rem'}}>
            {reading.cards.map((cardReading, idx) => (
              <TarotCard
                key={idx}
                card={cardReading.card}
                isReversed={cardReading.is_reversed}
                position={cardReading.position}
                testId={`reading-card-${idx}`}
              />
            ))}
          </div>
          
          <h3 style={{marginBottom: '1rem', fontSize: '1.5rem'}}>🔮 Interpretation:</h3>
          <div style={{
            background: 'rgba(139, 92, 246, 0.1)',
            padding: '1.5rem',
            borderRadius: '0.75rem',
            border: '2px solid rgba(139, 92, 246, 0.3)',
            lineHeight: '1.8',
            color: '#e0e7ff'
          }} data-testid="reading-interpretation">
            {reading.interpretation}
          </div>
          
          <div style={{marginTop: '2rem', display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap'}}>
            <button onClick={() => setReading(null)} className="btn btn-primary" data-testid="new-reading-button">
              ✨ New Reading
            </button>
            <button onClick={() => navigate('/history')} className="btn btn-secondary" data-testid="view-history-button">
              📜 View History
            </button>
          </div>
        </div>
        
        {/* Download/Share Options - Outside the captured area */}
        <div style={{marginTop: '2rem', display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap'}}>
          <button 
            onClick={handleDownloadImage} 
            className="btn btn-secondary"
            data-testid="download-image-button"
            title="Download as PNG image"
          >
            📸 Save as Image
          </button>
          <button 
            onClick={handleDownloadPDF} 
            className="btn btn-secondary"
            data-testid="download-pdf-button"
            title="Download as PDF"
          >
            📄 Save as PDF
          </button>
          <button 
            onClick={handleShare} 
            className="btn btn-secondary"
            data-testid="share-button"
            title="Share reading"
          >
            🔗 Share
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="container fade-in" data-testid="reading-form">
      <div className="mystical-bg"></div>
      <h1 className="text-center mb-4 glow-text" style={{fontSize: '3rem'}}>
        🔮 New Bitarot Reading
      </h1>
      
      <div className="card" style={{maxWidth: '700px', margin: '0 auto'}}>
        {error && <div className="error-message">{error}</div>}
        
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label">What guidance do you seek from the Bitarot?</label>
            <textarea
              className="form-textarea"
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              placeholder="Ask your question about crypto, life, or the universe..."
              required
              rows={4}
              data-testid="reading-question-input"
            />
          </div>
          
          <div className="form-group">
            <label className="form-label">Choose Your Spread:</label>
            <select
              className="form-select"
              value={spreadType}
              onChange={(e) => setSpreadType(e.target.value)}
              data-testid="reading-spread-select"
            >
              <optgroup label="Standard Spreads (All 78 Cards)">
                <option value="single">🎴 Single Card - Quick Answer</option>
                <option value="three_card">🎴🎴🎴 Three Card - Past, Present, Future</option>
                <option value="celtic_cross">✨ Celtic Cross - Comprehensive Reading (10 cards)</option>
              </optgroup>
              <optgroup label="Major Arcana Only (22 Cards - Life's Big Lessons)">
                <option value="major_arcana_three_card">👑 Major Arcana 3-Card - Life Themes</option>
                <option value="major_arcana_celtic_cross">🌟 Major Arcana Celtic Cross - Spiritual Journey</option>
              </optgroup>
            </select>
            <p style={{fontSize: '0.85rem', color: '#8b5cf6', marginTop: '0.5rem', fontStyle: 'italic'}}>
              {spreadType.includes('major_arcana') ? '✨ Major Arcana readings focus on significant life events, spiritual growth, and transformative moments.' : ''}
            </p>
          </div>
          
          <button 
            type="submit" 
            className="btn btn-primary" 
            style={{width: '100%', fontSize: '1.1rem'}}
            disabled={loading}
            data-testid="reading-submit-button"
          >
            {loading ? '🔮 Consulting the Oracle...' : '✨ Draw Cards'}
          </button>
        </form>
      </div>
    </div>
  );
}

// History Page
function History() {
  const [readings, setReadings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedReading, setSelectedReading] = useState(null);

  React.useEffect(() => {
    loadHistory();
  }, []);

  const loadHistory = async () => {
    try {
      const data = await api.getReadingHistory();
      setReadings(data);
    } catch (error) {
      console.error('Error loading history:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadHistoryImage = async () => {
    await downloadAsImage('history-reading-content', `bitarot-reading-${selectedReading.id}.png`);
  };

  const handleDownloadHistoryPDF = async () => {
    await downloadAsPDF('history-reading-content', `bitarot-reading-${selectedReading.id}.pdf`);
  };

  const handleShareHistory = async () => {
    await shareReading('history-reading-content', 'My Bitarot Reading');
  };

  if (selectedReading) {
    return (
      <div className="container fade-in" data-testid="history-detail">
        <div className="mystical-bg"></div>
        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap', gap: '1rem'}}>
          <button onClick={() => setSelectedReading(null)} className="btn btn-secondary" data-testid="back-to-history-button">
            ← Back to History
          </button>
          <div style={{display: 'flex', gap: '0.5rem', flexWrap: 'wrap'}}>
            <button onClick={handleDownloadHistoryImage} className="btn btn-secondary" title="Download as PNG">
              📸
            </button>
            <button onClick={handleDownloadHistoryPDF} className="btn btn-secondary" title="Download as PDF">
              📄
            </button>
            <button onClick={handleShareHistory} className="btn btn-secondary" title="Share">
              🔗
            </button>
          </div>
        </div>
        
        <div className="card" id="history-reading-content">
          <h2 style={{marginBottom: '1rem', color: '#8b5cf6'}}>Question:</h2>
          <p style={{fontSize: '1.2rem', marginBottom: '2rem', color: '#cbd5e1'}}>{selectedReading.question}</p>
          
          <div className={`grid ${selectedReading.cards.length > 3 ? 'grid-4' : 'grid-3'}`} style={{marginBottom: '2rem'}}>
            {selectedReading.cards.map((cardReading, idx) => (
              <TarotCard
                key={idx}
                card={cardReading.card}
                isReversed={cardReading.is_reversed}
                position={cardReading.position}
              />
            ))}
          </div>
          
          <h3 style={{marginBottom: '1rem'}}>Interpretation:</h3>
          <div style={{
            background: 'rgba(139, 92, 246, 0.1)',
            padding: '1.5rem',
            borderRadius: '0.75rem',
            lineHeight: '1.8'
          }}>
            {selectedReading.interpretation}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container fade-in" data-testid="history-list">
      <div className="mystical-bg"></div>
      <h1 className="text-center mb-4 glow-text" style={{fontSize: '3rem'}}>
        📜 Reading History
      </h1>
      
      {loading ? (
        <div className="spinner"></div>
      ) : readings.length === 0 ? (
        <div className="card text-center">
          <p style={{fontSize: '1.2rem', color: '#cbd5e1'}}>No readings yet. Start your journey!</p>
        </div>
      ) : (
        <div className="grid grid-2">
          {readings.map((reading) => (
            <div 
              key={reading.id} 
              className="card" 
              style={{cursor: 'pointer'}}
              onClick={() => setSelectedReading(reading)}
              data-testid={`history-item-${reading.id}`}
            >
              <h3 style={{marginBottom: '0.5rem', color: '#8b5cf6'}}>
                {reading.spread_type.replace('_', ' ').toUpperCase()}
              </h3>
              <p style={{marginBottom: '1rem', color: '#cbd5e1', fontStyle: 'italic'}}>
                "{reading.question.substring(0, 100)}{reading.question.length > 100 ? '...' : ''}"
              </p>
              <div style={{display: 'flex', gap: '0.5rem', flexWrap: 'wrap'}}>
                {reading.cards.slice(0, 3).map((card, idx) => (
                  <span key={idx} style={{fontSize: '1.5rem'}}>
                    {card.card.image_symbol}
                  </span>
                ))}
                {reading.cards.length > 3 && <span style={{color: '#8b5cf6'}}>+{reading.cards.length - 3} more</span>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Card Library Page
function CardLibrary() {
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCard, setSelectedCard] = useState(null);
  const [filter, setFilter] = useState('all');

  React.useEffect(() => {
    loadCards();
  }, []);

  const loadCards = async () => {
    try {
      const data = await api.getAllCards();
      setCards(data);
    } catch (error) {
      console.error('Error loading cards:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredCards = cards.filter(card => {
    if (filter === 'all') return true;
    if (filter === 'major') return card.arcana === 'major';
    if (filter === 'minor') return card.arcana === 'minor';
    return card.suit === filter;
  });

  if (selectedCard) {
    return (
      <div className="container fade-in" data-testid="card-detail">
        <div className="mystical-bg"></div>
        <button onClick={() => setSelectedCard(null)} className="btn btn-secondary mb-4" data-testid="back-to-library-button">
          ← Back to Library
        </button>
        
        <div className="card" style={{maxWidth: '800px', margin: '0 auto'}}>
          <div style={{marginBottom: '2rem'}}>
            <TarotCard card={selectedCard} />
          </div>
          
          <h3 style={{marginBottom: '1rem', color: '#8b5cf6'}}>Crypto Theme:</h3>
          <p style={{marginBottom: '2rem', color: '#cbd5e1'}}>{selectedCard.crypto_theme}</p>
          
          <h3 style={{marginBottom: '1rem', color: '#22c55e'}}>Upright Meaning:</h3>
          <p style={{marginBottom: '2rem', color: '#cbd5e1'}}>{selectedCard.upright_meaning}</p>
          
          <h3 style={{marginBottom: '1rem', color: '#ef4444'}}>Reversed Meaning:</h3>
          <p style={{color: '#cbd5e1'}}>{selectedCard.reversed_meaning}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container fade-in" data-testid="card-library">
      <div className="mystical-bg"></div>
      <h1 className="text-center mb-4 glow-text" style={{fontSize: '3rem'}}>
        📚 Bitarot Card Library
      </h1>
      
      <div className="mb-4 text-center">
        <select 
          className="form-select" 
          style={{maxWidth: '300px', margin: '0 auto'}}
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          data-testid="card-filter-select"
        >
          <option value="all">All Cards (78)</option>
          <option value="major">Major Arcana (22)</option>
          <option value="minor">Minor Arcana (56)</option>
          <option value="cups">Cups Suit</option>
          <option value="pentacles">Pentacles Suit</option>
          <option value="swords">Swords Suit</option>
          <option value="wands">Wands Suit</option>
        </select>
      </div>
      
      {loading ? (
        <div className="spinner"></div>
      ) : (
        <div className="grid grid-4">
          {filteredCards.map((card) => (
            <TarotCard
              key={card.id}
              card={card}
              onClick={() => setSelectedCard(card)}
              testId={`card-${card.id}`}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// Main App Component
function AppContent() {
  return (
    <div className="App">
      <Navigation />
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/reading" element={<Reading />} />
        <Route path="/history" element={<History />} />
        <Route path="/cards" element={<CardLibrary />} />
        <Route path="/subscription" element={<Subscription />} />
        <Route path="/admin" element={<AdminPanel />} />
        <Route path="/crypto-guide" element={<CryptoTutorial />} />
        <Route path="/settings" element={<Settings />} />
      </Routes>
      <InstallPWA />
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
